/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react/prop-types */
import currency from "currency.js";
import { TFunction } from "i18next";
import moment from "moment";
import { useCallback, useMemo } from "react";
import { UseFormSetValue, UseFormTrigger } from "react-hook-form";
import {
  GetMarkets200Response,
  InstrumentTypeResponse,
  OptionTypeResponse,
  SideResponse,
} from "../../../../codegen-api";
import { IDropdownItemsItem } from "../../../../components/shared/Dropdown";
import TickerLogo from "../../../../components/shared/TickerLogo";
import { COLORS } from "../../../../constants/design/colors";
import { nanosToSeconds } from "../../../../utils/date";
import { optionAssets } from "./helpers";
import { AssetTag } from "./style";
import { IFormStrategy, IFormValues } from "./types";

interface IUseStrategyDropdownsProps {
  strategies: IFormStrategy[];
  setValue: UseFormSetValue<IFormValues>;
  trigger: UseFormTrigger<IFormValues>;
  t: TFunction<"app", "pages.RFQPage.CreateRFQ">;
  activeOptionMarkets: GetMarkets200Response[] | undefined;
  activePerpMarkets: GetMarkets200Response[] | undefined;
}

export function useStrategyDropdowns({
  strategies,
  setValue,
  trigger,
  t,
  activeOptionMarkets,
  activePerpMarkets,
}: IUseStrategyDropdownsProps) {
  const instrumentTypes: InstrumentTypeResponse[] = useMemo(
    () =>
      Object.values(InstrumentTypeResponse)
        .filter((v) => v !== "SPOT")
        .map((instrumentType) => instrumentType),
    []
  );
  const sides: SideResponse[] = useMemo(
    () => Object.values(SideResponse).map((side) => side),
    []
  );
  const optionTypes: OptionTypeResponse[] = useMemo(
    () => Object.values(OptionTypeResponse).map((optionType) => optionType),
    []
  );
  const assets: string[] = useMemo(() => {
    const assetSet = new Set<string>();
    const allMarkets = [
      ...(activeOptionMarkets || []),
      ...(activePerpMarkets || []),
    ];

    allMarkets.forEach((market) => {
      assetSet.add(market.underlying_asset as string);
    });

    return Array.from(assetSet)
      .sort((a, b) => {
        // Ensure option assets are at the top of the list, then sort alphabetically
        if (optionAssets.includes(a) && !optionAssets.includes(b)) {
          return -1;
        }
        if (!optionAssets.includes(a) && optionAssets.includes(b)) {
          return 1;
        }
        return a.localeCompare(b);
      })
      .map((asset) => asset);
  }, [activeOptionMarkets, activePerpMarkets]);

  const prepareInstrumentTypeDropdown = useCallback(
    (index: number) =>
      instrumentTypes
        .filter(
          // Filter based on strategy's asset. If it is an optionAsset, remove Perpetual from list of instrument types
          // Otherwise, allow all instruments
          (instrument) => {
            if (
              strategies[index].asset !== undefined &&
              !optionAssets.includes(strategies[index].asset as string)
            ) {
              return instrument === InstrumentTypeResponse.Perpetual;
            }
            return true;
          }
        )
        .map(
          (instrument) =>
            ({
              label: t(instrument.toLowerCase()),
              active: strategies[index].instrument_type === instrument,
              onSelect: () => {
                // If selected strategy's asset is an optionAsset, remove Perpetual form list of instrument types
                if (
                  strategies[index].instrument_type !== undefined &&
                  !optionAssets.includes(strategies[index].asset as string)
                ) {
                  setValue(
                    `strategies.${index}.instrument_type`,
                    InstrumentTypeResponse.Perpetual
                  );
                  setValue(`strategies.${index}.option_type`, undefined);
                } else {
                  setValue(`strategies.${index}.instrument_type`, instrument);
                }

                if (
                  strategies[index].instrument_type ===
                  InstrumentTypeResponse.Perpetual
                ) {
                  setValue(`strategies.${index}.expiry`, undefined);
                  setValue(`strategies.${index}.strike`, undefined);
                  setValue(`strategies.${index}.option_type`, undefined);

                  trigger(`strategies.${index}.expiry`);
                  trigger(`strategies.${index}.strike`);
                }

                trigger(`strategies.${index}.option_type`);
                trigger(`strategies.${index}.instrument_type`);
              },
            } as IDropdownItemsItem)
        ),
    [instrumentTypes, setValue, strategies, t, trigger]
  );

  const prepareSidesDropdown = useCallback(
    (index: number) =>
      sides.map(
        (side) =>
          ({
            label: (
              <div
                style={{
                  color:
                    side === SideResponse.Buy
                      ? COLORS.up.one
                      : COLORS.down.one,
                }}
              >
                {t(side)}
              </div>
            ),
            active: strategies[index].side === side,
            onSelect: () => {
              setValue(`strategies.${index}.side`, side);
              trigger(`strategies.${index}.side`);
            },
          } as IDropdownItemsItem)
      ),
    [sides, setValue, strategies, trigger, t]
  );

  const prepareOptionTypeDropdown = useCallback(
    (index: number) =>
      optionTypes.map(
        (optionType) =>
          ({
            label: t(optionType),
            active: strategies[index].option_type === optionType,
            onSelect: () => {
              setValue(`strategies.${index}.option_type`, optionType);
              trigger(`strategies.${index}.option_type`);
            },
          } as IDropdownItemsItem)
      ),
    [optionTypes, setValue, strategies, trigger, t]
  );

  const prepareAssetDropdown = useCallback(
    (index: number) =>
      assets.map(
        (asset) =>
          ({
            label: (
              <AssetTag>
                <TickerLogo asset={asset} size={24} />
                <div>{asset}</div>
              </AssetTag>
            ),
            active: strategies[index].asset === asset,
            onSelect: () => {
              setValue(`strategies.${index}.asset`, asset);

              if (
                strategies[index].asset !== undefined &&
                !optionAssets.includes(strategies[index].asset as string)
              ) {
                // If the asset is not an optionAsset, reset the instrument type to perpetual and option_type to undefined
                setValue(
                  `strategies.${index}.instrument_type`,
                  InstrumentTypeResponse.Perpetual
                );
                setValue(`strategies.${index}.option_type`, undefined);
              }

              trigger(`strategies.${index}.asset`);
              trigger(`strategies.${index}.instrument_type`);
              trigger(`strategies.${index}.option_type`);

              setValue(`strategies.${index}.expiry`, undefined);
              setValue(`strategies.${index}.strike`, undefined);
            },
          } as IDropdownItemsItem)
      ),
    [assets, setValue, strategies, trigger]
  );

  const prepareExpiryDropdown = useCallback(
    (index: number) => {
      if (!activeOptionMarkets) return [];

      const selectedMarkets = activeOptionMarkets?.filter(
        (m) =>
          m.underlying_asset === strategies[index].asset &&
          m.expiry !== undefined
      );
      const marketsSet = new Set<string>();
      selectedMarkets.forEach((m) => marketsSet.add(String(m.expiry)));

      return Array.from(marketsSet).map(
        (m) =>
          ({
            label: moment.unix(nanosToSeconds(m)).format("DD MMM YY"),
            active: strategies[index].expiry === String(m),
            onSelect: () => {
              setValue(`strategies.${index}.expiry`, String(m));
              trigger(`strategies.${index}.expiry`);
            },
          } as IDropdownItemsItem)
      );
    },
    [activeOptionMarkets, setValue, strategies, trigger]
  );

  const prepareStrikeDropdown = useCallback(
    (index: number) => {
      if (!activeOptionMarkets) return [];

      const selectedMarkets = activeOptionMarkets?.filter(
        (m) =>
          m.underlying_asset === strategies[index].asset &&
          m.expiry === strategies[index].expiry
      );
      const marketsSet = new Set<string>();
      selectedMarkets.forEach((m) => marketsSet.add(String(m.strike)));

      return Array.from(marketsSet).map(
        (m) =>
          ({
            label: currency(String(m)).format(),
            active: strategies[index].strike === String(m),
            onSelect: () => {
              setValue(`strategies.${index}.strike`, String(m));
              trigger(`strategies.${index}.strike`);
            },
          } as IDropdownItemsItem)
      );
    },
    [activeOptionMarkets, setValue, strategies, trigger]
  );

  return {
    prepareInstrumentTypeDropdown,
    prepareSidesDropdown,
    prepareOptionTypeDropdown,
    prepareAssetDropdown,
    prepareExpiryDropdown,
    prepareStrikeDropdown,
  };
}
